import { useNode } from "@craftjs/core";
import { FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, Switch, TextField } from "@mui/material";
import { MediaManager, SettingCard } from "@/components";
import BorderRadiusSettings from "@/components/editor/settings/BorderRadius/BorderRadiusSettings";
import DimentionSettings from "@/components/editor/settings/DimentionSettings/DimentionSettings";
import SpacingSettings from "@/components/editor/settings/SpacingSettings/SpacingSettings";
import ClassSettings from "@/components/editor/settings/ClassSettings";
import useResponsiveValues, { ResponsiveValue } from "@/components/editor/hooks/useResponsiveValues";

enum ObjectFit {
  NONE = "none",
  FILL = "fill",
  CONTAIN = "contain",
  COVER = "cover",
  SCALE_DOWN = "scale-down",
}

enum ObjectPosition {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  CENTER = "center",
}

export interface ImageProps {
  src?: string | string[] | ResponsiveValue<string | string[]>;
  objectFit?: ObjectFit | ResponsiveValue<ObjectFit>;
  objectPosition?: ObjectPosition | ResponsiveValue<ObjectPosition>;
  modal?: boolean | ResponsiveValue<boolean>;
  href?: string;
  target?: "_self" | "_blank";
}

const ImageSource = () => {
  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const responsive = useResponsiveValues();

  return (
    <Stack spacing={2}>
      <Stack spacing={2} direction="row">
        <Stack flex={1}>
          <MediaManager
            value={responsive.getValue(props.src)}
            onChange={(value: string[]) => {
              setProp((props: ImageProps) => {
                props.src = responsive.setValue(props.src, value[0]);
              });
            }}
          />
        </Stack>
        <Stack flex={1} justifyContent="center" alignItems="center">
          <FormControlLabel
            label="User modal?"
            labelPlacement="start"
            control={
              <Switch
                checked={props?.modal}
                onChange={(e) => {
                  const useModal = e.target.checked;

                  setProp((props: ImageProps) => {
                    props.modal = useModal;
                  });
                }}
              />
            }
          />
        </Stack>
      </Stack>
      <TextField
        label="url"
        size="small"
        value={responsive.getValue(props.src)}
        onChange={(e) => {
          setProp((props: ImageProps) => {
            props.src = responsive.setValue(props.src, e.target.value);
            return props;
          });
        }}
      />
      <Stack direction="row" spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Posición</InputLabel>
          <Select
            label="Posición"
            value={responsive.getValue(props.objectPosition)}
            onChange={(e) => {
              setProp((props: ImageProps) => {
                props.objectPosition = responsive.setValue(props.objectPosition, e.target.value);
                return props;
              });
            }}
          >
            <MenuItem value={ObjectPosition.TOP}>Superior</MenuItem>
            <MenuItem value={ObjectPosition.BOTTOM}>Inferior</MenuItem>
            <MenuItem value={ObjectPosition.LEFT}>Izquierda</MenuItem>
            <MenuItem value={ObjectPosition.RIGHT}>Derecha</MenuItem>
            <MenuItem value={ObjectPosition.CENTER}>Centro</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Ajuste</InputLabel>
          <Select
            label="Ajuste"
            value={responsive.getValue(props.objectFit)}
            onChange={(e) => {
              setProp((props: ImageProps) => {
                props.objectFit = responsive.setValue(props.objectFit, e.target.value);
                return props;
              });
            }}
            defaultValue=""
          >
            <MenuItem value="">Ninguno</MenuItem>
            <MenuItem value={ObjectFit.FILL}>Rellenar</MenuItem>
            <MenuItem value={ObjectFit.CONTAIN}>Contener</MenuItem>
            <MenuItem value={ObjectFit.COVER}>Cubrir</MenuItem>
            <MenuItem value={ObjectFit.SCALE_DOWN}>Escalar hacia abajo</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={2}>
        <TextField
          label="Enlace"
          size="small"
          value={props.href}
          onChange={(e) => {
            setProp((props: ImageProps) => {
              props.href = e.target.value;
              return props;
            });
          }}
        />
        <InputLabel>Destino</InputLabel>
        <RadioGroup
          row
          color="primary"
          value={responsive.getValue(props.target) ?? ""}
          defaultValue={"_blank"}
          onChange={(_, value) => {
            setProp((props: ImageProps) => {
              props.target = responsive.setValue(props.target, value);
              return props;
            });
          }}
        >
          <FormControlLabel control={<Radio />} value={"_self"} label="Interno" />
          <FormControlLabel control={<Radio />} value={"_blank"} label="Externo" />
        </RadioGroup>
      </Stack>
    </Stack>
  );
};

const ImageSettings = () => {
  return (
    <Stack spacing={2}>
      {/* ✅ */}
      <SettingCard title="Clases CSS">
        <ClassSettings />
      </SettingCard>
      <SettingCard title="Source">
        <ImageSource />
      </SettingCard>
      <SettingCard title="Bordes Redondeados">
        <BorderRadiusSettings />
      </SettingCard>
      <SettingCard title="Dimensiones">
        <DimentionSettings />
      </SettingCard>
      <SettingCard title="Espaciado">
        <SpacingSettings />
      </SettingCard>
    </Stack>
  );
};

export default ImageSettings;
